import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import BranchSelect from "../../../components/ui/BranchSelect";
import { ASSET_CONDITIONS } from "./AssetBadges";
import {
  useAssetTypes,
  useCreateAsset,
  useUpdateAsset,
} from "../../../api/queries/assetQueries";
import { useCompanies } from "../../../api/queries/organizationQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../../utils/apiError";
import usePermissions from "../../../hooks/usePermissions";

const EMPTY_FORM = {
  companyId: "",
  branchId: "",
  assetTypeId: "",
  name: "",
  assetTag: "",
  serialNumber: "",
  brand: "",
  model: "",
  specifications: "",
  condition: "New",
  purchaseDate: "",
  purchasePrice: "",
  supplier: "",
  warrantyExpiryDate: "",
  remarks: "",
};

const EDITABLE_FIELDS = [
  "name",
  "assetTypeId",
  "assetTag",
  "serialNumber",
  "brand",
  "model",
  "specifications",
  "condition",
  "purchaseDate",
  "purchasePrice",
  "supplier",
  "warrantyExpiryDate",
  "remarks",
];

const assetToForm = (asset) =>
  Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, asset?.[k] ?? ""]));

/**
 * Register (asset = null) or edit an asset in a side drawer.
 * Company and branch are only set when registering; branch moves have their own dialog.
 */
const AssetFormDrawer = ({ asset, onClose, onSaved }) => {
  const isEdit = !!asset;
  const user = useCurrentUser();
  // HR-level (assets with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("assets");
  const [form, setForm] = useState(() =>
    isEdit
      ? assetToForm(asset)
      : {
          ...EMPTY_FORM,
          companyId: user.companyId || "",
          branchId: isCompanyWide ? "" : user.branchId || "",
        },
  );
  const [errors, setErrors] = useState([]);
  const { data: types = [] } = useAssetTypes(false);
  const { data: companies = [] } = useCompanies();
  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset();
  const busy = createAsset.isPending || updateAsset.isPending;
  const branchRequired = !isCompanyWide; // branch managers must pick a branch

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Keep the current type selectable when editing, even if it was deactivated
  const typeOptions =
    isEdit && asset.assetType && !types.some((t) => t.id === asset.assetTypeId)
      ? [
          ...types,
          { ...asset.assetType, name: `${asset.assetType.name} (inactive)` },
        ]
      : types;

  const validate = () => {
    const problems = [];
    if (!isEdit && !form.companyId) problems.push("Company is required.");
    if (!isEdit && branchRequired && !form.branchId)
      problems.push("Branch is required.");
    if (!form.assetTypeId) problems.push("Type is required.");
    if (!form.name.trim()) problems.push("Name is required.");
    if (
      form.purchasePrice !== "" &&
      (Number.isNaN(Number(form.purchasePrice)) ||
        Number(form.purchasePrice) < 0)
    )
      problems.push("Price must be a positive number.");
    if (
      form.purchaseDate &&
      form.warrantyExpiryDate &&
      form.warrantyExpiryDate < form.purchaseDate
    )
      problems.push("Warranty expiry can't be before the purchase date.");
    return problems;
  };

  const submit = (e) => {
    e.preventDefault();
    const problems = validate();
    setErrors(problems);
    if (problems.length) return;

    const fields = isEdit ? EDITABLE_FIELDS : Object.keys(EMPTY_FORM);
    const body = Object.fromEntries(
      fields.map((k) => [
        k,
        typeof form[k] === "string" ? form[k].trim() : form[k],
      ]),
    );
    body.purchasePrice =
      form.purchasePrice === "" ? "" : Number(form.purchasePrice);
    // Empty tag: on register the server generates one; on edit keep the current tag
    if (!body.assetTag) delete body.assetTag;

    const options = {
      onSuccess: (saved) => {
        toast.success(
          isEdit
            ? "Asset updated."
            : `Asset registered as ${saved?.assetTag || "new asset"}.`,
        );
        onSaved?.(saved);
        onClose();
      },
      onError: (err) => {
        const msg = err?.response?.data?.message;
        setErrors(
          Array.isArray(msg)
            ? msg
            : [getApiErrorMessage(err, "Could not save the asset.")],
        );
      },
    };
    if (isEdit) updateAsset.mutate({ id: asset.id, body }, options);
    else createAsset.mutate(body, options);
  };

  const fieldId = (k) => `asset-form-${k}`;

  return (
    <div className="app-drawer-backdrop" onClick={() => !busy && onClose()}>
      <aside
        className="app-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <form className="d-flex flex-column h-100" onSubmit={submit} noValidate>
          <div className="app-dialog-header">
            <div>
              <h5 id="app-drawer-title" className="fw-bold text-navy m-0">
                {isEdit ? "Edit asset" : "Register asset"}
              </h5>
              {isEdit && (
                <div className="text-slate-500 fs-080 mt-1">
                  {asset.assetTag}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              disabled={busy}
            ></button>
          </div>

          <div className="app-drawer-body">
            {errors.length > 0 && (
              <div className="alert alert-danger fs-085 py-2" role="alert">
                <ul className="mb-0 ps-3">
                  {errors.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </div>
            )}

            {!isEdit && (
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label
                    htmlFor={fieldId("companyId")}
                    className="profile-label"
                  >
                    Company <span className="text-danger">*</span>
                  </label>
                  <select
                    id={fieldId("companyId")}
                    className="form-select"
                    value={form.companyId}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        companyId: e.target.value,
                        branchId: "",
                      }))
                    }
                    disabled={!isCompanyWide || companies.length <= 1}
                  >
                    <option value="">Select company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.companyName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label
                    htmlFor={fieldId("branchId")}
                    className="profile-label"
                  >
                    Branch{" "}
                    {branchRequired && <span className="text-danger">*</span>}
                  </label>
                  <BranchSelect
                    id={fieldId("branchId")}
                    companyId={form.companyId}
                    value={form.branchId}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, branchId: v || "" }))
                    }
                    emptyLabel="Company level (no branch)"
                    allowEmpty={!branchRequired}
                  />
                </div>
              </div>
            )}

            <div className="row g-3">
              <div className="col-md-6">
                <label
                  htmlFor={fieldId("assetTypeId")}
                  className="profile-label"
                >
                  Type <span className="text-danger">*</span>
                </label>
                <select
                  id={fieldId("assetTypeId")}
                  className="form-select"
                  value={form.assetTypeId}
                  onChange={set("assetTypeId")}
                >
                  <option value="">Select type</option>
                  {typeOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.code ? ` (${t.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label htmlFor={fieldId("assetTag")} className="profile-label">
                  Asset tag
                </label>
                <input
                  id={fieldId("assetTag")}
                  className="form-control"
                  value={form.assetTag}
                  onChange={set("assetTag")}
                  placeholder="Auto-generated"
                />
              </div>
              <div className="col-12">
                <label htmlFor={fieldId("name")} className="profile-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  id={fieldId("name")}
                  className="form-control"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="e.g. Dell Latitude 5440"
                />
              </div>
              <div className="col-md-4">
                <label htmlFor={fieldId("brand")} className="profile-label">
                  Brand
                </label>
                <input
                  id={fieldId("brand")}
                  className="form-control"
                  value={form.brand}
                  onChange={set("brand")}
                />
              </div>
              <div className="col-md-4">
                <label htmlFor={fieldId("model")} className="profile-label">
                  Model
                </label>
                <input
                  id={fieldId("model")}
                  className="form-control"
                  value={form.model}
                  onChange={set("model")}
                />
              </div>
              <div className="col-md-4">
                <label
                  htmlFor={fieldId("serialNumber")}
                  className="profile-label"
                >
                  Serial number
                </label>
                <input
                  id={fieldId("serialNumber")}
                  className="form-control"
                  value={form.serialNumber}
                  onChange={set("serialNumber")}
                />
              </div>
              <div className="col-12">
                <label
                  htmlFor={fieldId("specifications")}
                  className="profile-label"
                >
                  Specifications
                </label>
                <textarea
                  id={fieldId("specifications")}
                  className="form-control"
                  rows={2}
                  value={form.specifications}
                  onChange={set("specifications")}
                  placeholder="e.g. i5-1345U, 16GB RAM, 512GB SSD"
                />
              </div>
              <div className="col-md-4">
                <label htmlFor={fieldId("condition")} className="profile-label">
                  Condition
                </label>
                <select
                  id={fieldId("condition")}
                  className="form-select"
                  value={form.condition}
                  onChange={set("condition")}
                >
                  {ASSET_CONDITIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label
                  htmlFor={fieldId("purchaseDate")}
                  className="profile-label"
                >
                  Purchase date
                </label>
                <input
                  id={fieldId("purchaseDate")}
                  type="date"
                  className="form-control"
                  value={form.purchaseDate}
                  onChange={set("purchaseDate")}
                />
              </div>
              <div className="col-md-4">
                <label
                  htmlFor={fieldId("purchasePrice")}
                  className="profile-label"
                >
                  Price (₱)
                </label>
                <input
                  id={fieldId("purchasePrice")}
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={form.purchasePrice}
                  onChange={set("purchasePrice")}
                />
              </div>
              <div className="col-md-8">
                <label htmlFor={fieldId("supplier")} className="profile-label">
                  Supplier
                </label>
                <input
                  id={fieldId("supplier")}
                  className="form-control"
                  value={form.supplier}
                  onChange={set("supplier")}
                />
              </div>
              <div className="col-md-4">
                <label
                  htmlFor={fieldId("warrantyExpiryDate")}
                  className="profile-label"
                >
                  Warranty expiry
                </label>
                <input
                  id={fieldId("warrantyExpiryDate")}
                  type="date"
                  className="form-control"
                  value={form.warrantyExpiryDate}
                  onChange={set("warrantyExpiryDate")}
                />
              </div>
              <div className="col-12">
                <label htmlFor={fieldId("remarks")} className="profile-label">
                  Remarks
                </label>
                <textarea
                  id={fieldId("remarks")}
                  className="form-control"
                  rows={2}
                  value={form.remarks}
                  onChange={set("remarks")}
                />
              </div>
            </div>
          </div>

          <div className="app-dialog-footer">
            <button
              type="button"
              className="btn btn-outline-secondary profile-btn"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary profile-btn"
              disabled={busy}
            >
              {busy && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              )}
              {isEdit ? "Save changes" : "Register asset"}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
};

export default AssetFormDrawer;
